import React, { useState } from 'react';
import { WorkflowRule, WorkflowCondition, ConditionOperator } from '../../../../types/workflowRules';
import { Plus, Trash2, AlertCircle, Info } from 'lucide-react';
import { getObjectFieldsFromManager, getPicklistValuesFromManager } from '../../../../utils/workflowFieldHelper';

interface WizardStep2Props {
  formData: Partial<WorkflowRule>;
  updateFormData: (updates: Partial<WorkflowRule>) => void;
  onNext: () => void;
  onBack: () => void;
}

// Operator options with labels
const OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'doesNotContain', label: 'Does Not Contain' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
  { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
  { value: 'isEmpty', label: 'Is Empty' },
  { value: 'isNotEmpty', label: 'Is Not Empty' }
];

// Get fields for selected object from Object Manager
const getObjectFields = (objectApiName: string) => {
  return getObjectFieldsFromManager(objectApiName);
};

// Get picklist values for a field from Object Manager
const getPicklistValues = (objectApiName: string, fieldApiName: string): string[] => {
  return getPicklistValuesFromManager(objectApiName, fieldApiName);
};

export const WizardStep2Criteria: React.FC<WizardStep2Props> = ({
  formData,
  updateFormData
}) => {
  const [showPreview, setShowPreview] = useState(false);

  // Initialize criteria if not exists
  if (!formData.evaluationCriteria) {
    updateFormData({
      evaluationCriteria: {
        operator: 'AND',
        conditions: []
      }
    });
  }

  const criteria = formData.evaluationCriteria!;
  const objectFields = formData.objectApiName ? getObjectFields(formData.objectApiName) : [];

  // Add new condition
  const handleAddCondition = () => {
    const newCondition: WorkflowCondition = {
      id: `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      fieldApiName: '',
      operator: 'equals',
      value: ''
    };

    updateFormData({
      evaluationCriteria: {
        ...criteria,
        conditions: [...criteria.conditions, newCondition]
      }
    });
  };

  // Update condition
  const handleUpdateCondition = (conditionId: string, updates: Partial<WorkflowCondition>) => {
    const updatedConditions = criteria.conditions.map(cond =>
      cond.id === conditionId ? { ...cond, ...updates } : cond
    );

    updateFormData({
      evaluationCriteria: {
        ...criteria,
        conditions: updatedConditions
      }
    });
  };

  // Remove condition
  const handleRemoveCondition = (conditionId: string) => {
    const updatedConditions = criteria.conditions.filter(cond => cond.id !== conditionId);

    updateFormData({
      evaluationCriteria: {
        ...criteria,
        conditions: updatedConditions
      }
    });
  };

  // Toggle AND/OR operator
  const handleToggleOperator = () => {
    updateFormData({
      evaluationCriteria: {
        ...criteria,
        operator: criteria.operator === 'AND' ? 'OR' : 'AND'
      }
    });
  };

  // Generate preview text
  const generatePreview = (): string => {
    if (criteria.conditions.length === 0) {
      return 'No conditions defined yet. Click "Add Condition" to start building your criteria.';
    }

    const conditionTexts = criteria.conditions.map((cond, index) => {
      const field = objectFields.find(f => f.apiName === cond.fieldApiName);
      const fieldLabel = field?.label || cond.fieldApiName;
      const operator = OPERATORS.find(op => op.value === cond.operator)?.label || cond.operator;
      const value = cond.value || '(not set)';

      return `${index + 1}. ${fieldLabel} ${operator} "${value}"`;
    });

    const logicText = criteria.operator === 'AND'
      ? 'ALL of the following conditions must be true:'
      : 'ANY of the following conditions must be true:';

    return `${logicText}\n\n${conditionTexts.join('\n')}`;
  };

  // Check if condition needs value input
  const needsValueInput = (operator: ConditionOperator): boolean => {
    return !['isEmpty', 'isNotEmpty'].includes(operator);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Define Rule Criteria
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Specify when this workflow rule should run by defining conditions
        </p>
      </div>

      {/* Object Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="font-medium mb-1">Building criteria for: {formData.objectApiName}</div>
            <div>
              Select fields from the {formData.objectApiName} object to create conditions.
              Use AND logic when all conditions must be true, or OR logic when any condition can be true.
            </div>
          </div>
        </div>
      </div>

      {/* Criteria Logic Toggle */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Run this rule when:
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleOperator}
            className={`
              px-4 py-2 rounded-lg font-medium text-sm transition-colors
              ${criteria.operator === 'AND'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }
            `}
          >
            AND
          </button>
          <button
            onClick={handleToggleOperator}
            className={`
              px-4 py-2 rounded-lg font-medium text-sm transition-colors
              ${criteria.operator === 'OR'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }
            `}
          >
            OR
          </button>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {criteria.operator === 'AND' ? 'All conditions must be true' : 'Any condition can be true'}
        </span>
      </div>

      {/* Conditions List */}
      <div className="space-y-4">
        {criteria.conditions.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Conditions Yet
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Add at least one condition to define when this workflow rule should run
            </p>
            <button
              onClick={handleAddCondition}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add First Condition
            </button>
          </div>
        ) : (
          <>
            {criteria.conditions.map((condition, index) => {
              const field = objectFields.find(f => f.apiName === condition.fieldApiName);
              const showValueInput = needsValueInput(condition.operator);
              const isPicklist = field?.type === 'picklist';
              const picklistValues = isPicklist && condition.fieldApiName
                ? getPicklistValues(formData.objectApiName!, condition.fieldApiName)
                : [];

              return (
                <div
                  key={condition.id}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  {/* Condition Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Condition {index + 1}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveCondition(condition.id)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Remove condition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Condition Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Field Selection */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Field
                      </label>
                      <select
                        value={condition.fieldApiName}
                        onChange={(e) => handleUpdateCondition(condition.id, {
                          fieldApiName: e.target.value,
                          value: '' // Reset value when field changes
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select field...</option>
                        {objectFields.map(field => (
                          <option key={field.apiName} value={field.apiName}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Operator Selection */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Operator
                      </label>
                      <select
                        value={condition.operator}
                        onChange={(e) => handleUpdateCondition(condition.id, {
                          operator: e.target.value as ConditionOperator,
                          value: needsValueInput(e.target.value as ConditionOperator) ? condition.value : ''
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {OPERATORS.map(op => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Value Input */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Value
                      </label>
                      {!showValueInput ? (
                        <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                          (no value needed)
                        </div>
                      ) : isPicklist && picklistValues.length > 0 ? (
                        <select
                          value={condition.value}
                          onChange={(e) => handleUpdateCondition(condition.id, { value: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select value...</option>
                          {picklistValues.map(val => (
                            <option key={val} value={val}>{val}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field?.type === 'date' ? 'date' : field?.type === 'email' ? 'email' : 'text'}
                          value={condition.value}
                          onChange={(e) => handleUpdateCondition(condition.id, { value: e.target.value })}
                          placeholder="Enter value..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}
                    </div>
                  </div>

                  {/* AND/OR connector (not for last condition) */}
                  {index < criteria.conditions.length - 1 && (
                    <div className="mt-4 flex justify-center">
                      <div className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                        {criteria.operator}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add Condition Button */}
            <button
              onClick={handleAddCondition}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Condition
            </button>
          </>
        )}
      </div>

      {/* Preview */}
      {criteria.conditions.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              💡 Preview: How this rule will evaluate
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {showPreview ? 'Hide' : 'Show'}
            </span>
          </button>
          {showPreview && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                {generatePreview()}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        💡 Tip: You can proceed to the next step even without conditions.
        You can always come back and add them later.
      </div>
    </div>
  );
};
